import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Toast,
} from '@/components/ui';
import { formatRelative } from '@/lib/format';

interface Check {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
  fix?: string | null;
  advisory?: boolean;
}

interface Readiness {
  counts: Record<string, number>;
  mock_mode: Record<string, boolean>;
  checks: Check[];
  ready: boolean;
  blocking: string[];
}

interface Provider {
  id: string;
  kind: string;
  key: string;
  name: string;
  driver: string;
  enabled: boolean;
  is_default: boolean;
  health_state: string;
  health_checked_at: string | null;
  has_credentials: boolean;
  config: Record<string, unknown>;
}

interface MockMode {
  effective: Record<string, boolean>;
  env: Record<string, boolean>;
  overrides: Record<string, boolean>;
}

const FLAG_LABELS: Record<string, string> = {
  ai: 'Language model',
  stt: 'Speech to text',
  tts: 'Text to speech',
  telephony: 'Telephony (Asterisk)',
  vicidial: 'Vicidial write-back',
};

const FLAG_NOTES: Record<string, string> = {
  ai: 'The conversation itself. Costs per call.',
  stt: 'Hearing the caller. Costs per minute.',
  tts: 'The bot’s voice. Costs per character.',
  telephony: 'Real audio on real calls.',
  vicidial: 'Writes dispositions and DNC to real leads. Turn this on last.',
};

const KIND_LABELS: Record<string, string> = {
  LLM: 'Language model',
  STT: 'Speech to text',
  TTS: 'Text to speech',
  TELEPHONY: 'Telephony',
};

/**
 * Everything needed to get the platform working, on one screen.
 *
 * This page exists because the alternative was an SSH session: run the seed by
 * hand, edit /opt/glee/.env, restart the service. Each of those is a decision
 * the platform owner should be able to make here, while watching the result.
 */
export default function Setup() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);
  const [keys, setKeys] = useState<Record<string, string>>({});

  const readiness = useQuery({
    queryKey: ['admin-readiness'],
    queryFn: () => api.get<Readiness>('/admin/readiness'),
  });
  const providers = useQuery({
    queryKey: ['admin-provider-status'],
    queryFn: () => api.get<{ data: Provider[] }>('/admin/provider-status'),
  });
  const mock = useQuery({
    queryKey: ['admin-mock-mode'],
    queryFn: () => api.get<MockMode>('/admin/mock-mode'),
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-readiness'] });
    queryClient.invalidateQueries({ queryKey: ['admin-provider-status'] });
    queryClient.invalidateQueries({ queryKey: ['admin-mock-mode'] });
  };

  const seed = useMutation({
    mutationFn: (demo: boolean) =>
      api.post<{ ok: boolean; errors: Record<string, string> }>('/admin/seed', { demo }),
    onSuccess: (result) => {
      refreshAll();
      queryClient.invalidateQueries({ queryKey: ['admin-bot-templates'] });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      const failed = Object.entries(result.errors ?? {});
      setToast({
        message: result.ok
          ? 'Catalog, engines, providers and plans installed.'
          : `Partly installed. Failed: ${failed.map(([k, v]) => `${k} (${v})`).join('; ')}`,
        tone: result.ok ? 'good' : 'bad',
      });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Seed failed'), tone: 'bad' }),
  });

  const saveKey = useMutation({
    mutationFn: ({ id, apiKey }: { id: string; apiKey: string }) =>
      api.put<{ ok: boolean; provider: string }>(`/admin/providers/${id}/key`, {
        api_key: apiKey,
      }),
    onSuccess: (result, variables) => {
      setKeys((previous) => ({ ...previous, [variables.id]: '' }));
      refreshAll();
      setToast({ message: `${result.provider} key saved. Test it below.`, tone: 'good' });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not save the key'), tone: 'bad' }),
  });

  const testProvider = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; provider: string; detail: string }>(
        `/admin/providers/${id}/test`,
        {},
      ),
    onSuccess: (result) => {
      refreshAll();
      setToast({
        message: `${result.provider}: ${result.detail}`,
        tone: result.ok ? 'good' : 'bad',
      });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Test failed'), tone: 'bad' }),
  });

  const makeDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<{ kind: string; provider: string }>(`/admin/providers/${id}/default`, {}),
    onSuccess: (result) => {
      refreshAll();
      setToast({
        message: `${result.provider} is now the default ${KIND_LABELS[result.kind]?.toLowerCase() ?? result.kind}.`,
        tone: 'good',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not change the default'), tone: 'bad' }),
  });

  const setMock = useMutation({
    mutationFn: (changes: Record<string, boolean>) =>
      api.post<{ effective: Record<string, boolean>; warnings: string[] }>(
        '/admin/mock-mode',
        changes,
      ),
    onSuccess: (result) => {
      refreshAll();
      setToast({
        message: result.warnings.length
          ? result.warnings.join(' ')
          : 'Applied. Takes effect within about ten seconds — no restart.',
        tone: result.warnings.length ? 'bad' : 'good',
      });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not change mock mode'), tone: 'bad' }),
  });

  if (readiness.isPending) return <Loading label="Checking the platform…" />;
  if (readiness.error) {
    return <ErrorState error={readiness.error} retry={() => readiness.refetch()} />;
  }

  const checks = readiness.data.checks;
  const needsSeed = readiness.data.blocking.includes('seed');
  const testable = (providers.data?.data ?? []).filter((p) =>
    ['LLM', 'STT', 'TTS'].includes(p.kind),
  );

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Setup"
        description="Get the platform working, and turn the mocks off when you are ready."
        actions={
          <Button variant="secondary" onClick={refreshAll}>
            Refresh
          </Button>
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* 1. Readiness                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title="Where you are"
        description="Each of these silently blocks a later step if it is missing."
        padded={false}
      >
        <ul className="divide-y divide-line">
          {checks.map((check) => (
            <li key={check.key} className="flex items-start gap-3 px-4 py-3">
              <span
                aria-hidden="true"
                className={
                  check.ok
                    ? 'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                    : check.advisory
                      ? 'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
                      : 'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                }
              >
                {check.ok ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                    <path
                      fillRule="evenodd"
                      d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <span className="text-2xs font-bold">!</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink-900 dark:text-ink-50">
                  {check.label}
                </p>
                <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
                  {check.detail}
                </p>
              </div>
              {check.fix && !check.ok && (
                <Badge tone="amber">{check.fix}</Badge>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 2. Seed                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title="Service catalog and bot engines"
        description="Installs the 16 Home Services trades, the six bot engines, the providers and the plans."
        className="mt-4"
      >
        {needsSeed && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            <strong className="font-semibold">Nothing is installed yet.</strong> Until
            this runs, Services catalog, Bot engines and Providers are all empty, and
            creating a bot fails with “No active bot template found”.
          </div>
        )}
        <p className="text-sm text-ink-600 dark:text-ink-300">
          Safe to run as often as you like — every section is an upsert, so it fixes
          whatever is missing and leaves the rest alone. Your bots keep their own
          settings, because a bot stores only what differs from its engine.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button loading={seed.isPending && seed.variables === false} onClick={() => seed.mutate(false)}>
            Run seed
          </Button>
          <Button
            variant="secondary"
            loading={seed.isPending && seed.variables === true}
            onClick={() => seed.mutate(true)}
          >
            Run seed with demo client
          </Button>
        </div>
        <p className="mt-2 text-2xs text-ink-500 dark:text-ink-400">
          The demo client creates “ABC Home Services” with two Vicidial servers and
          ten bots on each — useful for clicking through, and safe to delete.
        </p>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 3. API keys                                                         */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title="Provider API keys"
        description="Stored encrypted and never shown again. Test each one to confirm the vendor accepted it."
        className="mt-4"
        padded={false}
      >
        {providers.isPending ? (
          <div className="space-y-2 p-4">
            <div className="skeleton h-14" />
            <div className="skeleton h-14" />
          </div>
        ) : testable.length === 0 ? (
          <div className="p-4 text-sm text-ink-500">
            No providers registered yet — run the seed above first.
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {testable.map((provider) => {
              const verified = provider.health_state === 'HEALTHY';
              const failed = provider.health_state === 'FAILED';
              return (
                <li key={provider.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
                          {provider.name}
                        </span>
                        <Badge tone="gray">{KIND_LABELS[provider.kind] ?? provider.kind}</Badge>
                        {provider.is_default && <Badge tone="blue">Default</Badge>}

                        {/* Three distinct states, because "a key is set" and
                            "the key works" are different facts and only one of
                            them means calls will succeed. */}
                        {verified ? (
                          <Badge tone="green">Connected</Badge>
                        ) : failed ? (
                          <Badge tone="red">Rejected</Badge>
                        ) : provider.has_credentials ? (
                          <Badge tone="amber">Key set, untested</Badge>
                        ) : (
                          <Badge tone="gray">No key</Badge>
                        )}
                      </div>
                      <p className="mt-1 font-mono text-2xs text-ink-500">
                        {provider.driver}
                        {typeof provider.config?.model === 'string' &&
                          ` · ${provider.config.model}`}
                        {provider.health_checked_at &&
                          ` · checked ${formatRelative(provider.health_checked_at)}`}
                      </p>
                    </div>

                    <div className="flex shrink-0 gap-1.5">
                      {/* Only offered where there is a real choice. Switching
                          the default only changes what a bot with no explicit
                          provider uses; a bot may still pin the other. */}
                      {!provider.is_default &&
                        testable.filter((p) => p.kind === provider.kind).length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={makeDefault.isPending && makeDefault.variables === provider.id}
                            onClick={() => makeDefault.mutate(provider.id)}
                          >
                            Make default
                          </Button>
                        )}
                      {provider.has_credentials && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={testProvider.isPending && testProvider.variables === provider.id}
                          onClick={() => testProvider.mutate(provider.id)}
                        >
                          Test connection
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <div className="min-w-[16rem] flex-1">
                      <Field label={provider.has_credentials ? 'Replace key' : 'API key'}>
                        <Input
                          type="password"
                          value={keys[provider.id] ?? ''}
                          onChange={(e) =>
                            setKeys((previous) => ({ ...previous, [provider.id]: e.target.value }))
                          }
                          placeholder={
                            provider.key === 'anthropic'
                              ? 'sk-ant-…'
                              : provider.key === 'openai'
                                ? 'sk-…'
                                : 'paste the key'
                          }
                          autoComplete="off"
                        />
                      </Field>
                    </div>
                    <Button
                      loading={saveKey.isPending && saveKey.variables?.id === provider.id}
                      disabled={!(keys[provider.id] ?? '').trim()}
                      onClick={() =>
                        saveKey.mutate({ id: provider.id, apiKey: keys[provider.id] ?? '' })
                      }
                    >
                      Save
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 4. Mock mode                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title="Going live"
        description="Turn each integration on when you are ready. Takes effect in about ten seconds — no restart."
        className="mt-4"
        padded={false}
      >
        {mock.isPending ? (
          <div className="space-y-2 p-4">
            <div className="skeleton h-12" />
          </div>
        ) : (
          <>
            <ul className="divide-y divide-line">
              {Object.entries(mock.data?.effective ?? {}).map(([flag, mocked]) => {
                const fromPortal = flag in (mock.data?.overrides ?? {});
                return (
                  <li key={flag} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
                          {FLAG_LABELS[flag] ?? flag}
                        </span>
                        {mocked ? (
                          <Badge tone="amber">Mocked</Badge>
                        ) : (
                          <Badge tone="green">Live</Badge>
                        )}
                        {/* Where the value came from decides where it is
                            undone — the portal, or /opt/glee/.env. */}
                        {!fromPortal && (
                          <Badge tone="gray">from .env</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-2xs text-ink-500 dark:text-ink-400">
                        {FLAG_NOTES[flag] ?? ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={mocked ? 'primary' : 'secondary'}
                      loading={setMock.isPending && flag in (setMock.variables ?? {})}
                      onClick={() => setMock.mutate({ [flag]: !mocked })}
                    >
                      {mocked ? 'Go live' : 'Back to mock'}
                    </Button>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-line p-4">
              <p className="text-xs leading-relaxed text-ink-600 dark:text-ink-300">
                Turn these on one at a time and place a test call after each. They fail
                differently, and a mock that is still on is invisible in the result —
                a provider with a rejected key falls back to the mock rather than
                failing the call, so the bot keeps working and keeps sounding like a
                bot. <strong>Vicidial write-back last:</strong> it is the only one that
                writes to real leads, and the only one you cannot undo.
              </p>
            </div>
          </>
        )}
      </Card>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
